import { useEffect, useState } from 'react';

import type { BillingStatus, RefreshAuthOptions } from '../../../context/AuthContext';
import type { useToast } from '../../../context/ToastContext';
import { trackAuthEvent } from '../../../services/analytics';
import { billingAPI } from '../../../services/api';
import { showDialog } from '../../../utils/dialogs';
import type { Translate } from '../profileTypes';
import { withdrawalAvailableUntil, withdrawalNeedsPricingCurrency } from '../withdrawal';

interface UseContractWithdrawalOptions {
  billingStatus: BillingStatus | null;
  refreshAuth: (options?: RefreshAuthOptions) => Promise<void>;
  loadProfile: () => Promise<void>;
  showToast: ReturnType<typeof useToast>['showToast'];
  t: Translate;
}

/**
 * EU "withdraw from contract here" (BIL-L23): whether it is offered, its confirm
 * step, and the withdrawal call.
 */
export function useContractWithdrawal({
  billingStatus,
  refreshAuth,
  loadProfile,
  showToast,
  t,
}: UseContractWithdrawalOptions) {
  const [pricingCurrency, setPricingCurrency] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const needsCurrency = withdrawalNeedsPricingCurrency(billingStatus);

  useEffect(() => {
    if (!needsCurrency || pricingCurrency) return;
    let cancelled = false;
    billingAPI
      .pricing()
      .then((response) => {
        if (!cancelled) setPricingCurrency(response.currency || null);
      })
      .catch(() => {
        // Without a currency the function is not offered; the server stays authoritative.
      });
    return () => {
      cancelled = true;
    };
  }, [needsCurrency, pricingCurrency]);

  const availableUntil = withdrawalAvailableUntil(billingStatus, pricingCurrency);
  const isTrial = (billingStatus?.subscription_status || '').toLowerCase() === 'trialing';

  const openConfirm = () => {
    trackAuthEvent('contract_withdrawal_opened', { subscription_status: billingStatus?.subscription_status });
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (!loading) setShowConfirm(false);
  };

  const confirmWithdrawal = async () => {
    setLoading(true);
    try {
      await billingAPI.withdrawFromContract();
      trackAuthEvent('contract_withdrawal_confirmed', { subscription_status: billingStatus?.subscription_status });
      setShowConfirm(false);
      showToast(t('profile.withdrawal.success'), 'success');
      await Promise.all([refreshAuth({ forceBillingRefresh: true }), loadProfile()]);
    } catch (error: any) {
      const code = error?.response?.data?.detail?.code;
      setShowConfirm(false);
      showDialog(
        t('common.error'),
        code === 'withdrawal_not_available' ? t('profile.withdrawal.notAvailable') : t('profile.withdrawal.failed'),
      );
    } finally {
      setLoading(false);
    }
  };

  return { availableUntil, isTrial, showConfirm, loading, openConfirm, closeConfirm, confirmWithdrawal };
}

export type ContractWithdrawalFlow = ReturnType<typeof useContractWithdrawal>;
